import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { useApi } from "shared/services/api/useApi";

export const useDailyTimesheet = () => {
    const { getDataAsync, postData } = useApi();
    const queryClient = useQueryClient();

    // Fetch employee's assigned projects
    const getAssignmentsApiCall = useCallback(
        async ({ employeeId, fromDate, weeksCount = 1 }) => {
            if (!fromDate) return null;

            const response = await getDataAsync("getAssignments", {
                urlParams: [
                    { name: "$employeeId", value: employeeId },
                    { name: "$fromDate", value: fromDate },
                    { name: "$weeksCount", value: weeksCount },
                ],
            });

            return response.status === 204 ? null : response.json();
        },
        [getDataAsync]
    );

    // Get daily totals for a date range
    const getDailyTotalsApiCall = useCallback(
        async ({ employeeId, fromDate, toDate, officeId, agencyId }) => {
            if (!employeeId || !fromDate || !toDate || !officeId || !agencyId)
                return null;

            const response = await getDataAsync("getDailyTotals", {
                urlParams: [
                    { name: "$employeeId", value: employeeId },
                    { name: "$fromDate", value: fromDate },
                    { name: "$toDate", value: toDate },
                    { name: "$officeId", value: officeId },
                    { name: "$agencyId", value: agencyId },
                ],
            });

            return response.status === 204 ? [] : response.json();
        },
        [getDataAsync]
    );

    // Get recent time entries by entered date
    const getRecentTimeEntriesApiCall = useCallback(
        async ({ userId, fromDate, toDate }) => {
            if (!userId || !fromDate || !toDate) return null;

            const response = await getDataAsync("getRecentTimeEntries", {
                urlParams: [
                    { name: "$userId", value: userId },
                    { name: "$fromDate", value: fromDate },
                    { name: "$toDate", value: toDate },
                ],
            });

            return response.status === 204 ? [] : response.json();
        },
        [getDataAsync]
    );

    // Check if timesheet is submitted for a date range
    const getTimesheetSubmittedApiCall = useCallback(
        async ({ employeeId, fromDate, toDate }) => {
            if (!employeeId || !fromDate || !toDate) return null;

            const response = await getDataAsync("checkTimesheetSubmitted", {
                urlParams: [
                    { name: "$employeeId", value: employeeId },
                    { name: "$fromDate", value: fromDate },
                    { name: "$toDate", value: toDate },
                ],
            });

            return response.status === 204 ? null : response.json();
        },
        [getDataAsync]
    );

    // Fetch department assignments for timesheet
    const getDepartmentAssignmentsApiCall = useCallback(
        async ({
            departmentId,
            officeId,
            agencyId,
            employeeId,
            fromDate,
            weeksCount = 1,
        }) => {
            if (
                !departmentId ||
                !officeId ||
                !agencyId ||
                !employeeId ||
                !fromDate
            )
                return null;

            const response = await getDataAsync("getAssignmentsDepartment", {
                urlParams: [
                    { name: "$departmentId", value: departmentId },
                    { name: "$officeId", value: officeId },
                    { name: "$agencyId", value: agencyId },
                    { name: "$employeeId", value: employeeId },
                    { name: "$fromDate", value: fromDate },
                    { name: "$weeksCount", value: weeksCount },
                ],
            });

            return response.status === 204 ? null : response.json();
        },
        [getDataAsync]
    );

    // Add hours for a project
    const updateHoursApiCall = useCallback(
        ({
            employeeId,
            projectId,
            currentLegacyTitleId,
            currentOfficeId,
            date,
            hours,
            agencyId,
            departmentId,
        }) => {
            return postData("addHoursByEmployee", {
                urlParams: [
                    { name: "$employeeId", value: employeeId },
                    { name: "$projectId", value: projectId },
                    {
                        name: "$currentLegacyTitleId",
                        value: currentLegacyTitleId,
                    },
                    { name: "$currentOfficeId", value: currentOfficeId },
                    { name: "$date", value: date },
                    { name: "$hours", value: hours },
                    { name: "$agencyId", value: agencyId },
                    { name: "$departmentId", value: departmentId },
                ],
            });
        },
        [postData]
    );

    // Mutation with cache invalidation
    const updateHoursMutation = useMutation(updateHoursApiCall, {
        onSuccess: () => {
            // Invalidate to refresh data after save
            queryClient.invalidateQueries("dailyTimesheetTotals");
            queryClient.invalidateQueries("dailyTimesheetRecentEntries");
        },
    });

    return useMemo(() => {
        const useAssignments = (dataObj) =>
            useQuery(
                ["dailyTimesheetAssignments", dataObj],
                () => getAssignmentsApiCall(dataObj),
                { enabled: !!dataObj.employeeId && !!dataObj.fromDate }
            );

        const useDailyTotals = (dataObj) =>
            useQuery({
                queryKey: ["dailyTimesheetTotals", dataObj],
                queryFn: () => getDailyTotalsApiCall(dataObj),
                refetchOnMount: true,
                enabled:
                    !!dataObj.employeeId &&
                    !!dataObj.fromDate &&
                    !!dataObj.toDate &&
                    !!dataObj.officeId,
            });

        const useRecentEntries = (dataObj) =>
            useQuery({
                queryKey: ["dailyTimesheetRecentEntries", dataObj],
                queryFn: () => getRecentTimeEntriesApiCall(dataObj),
                refetchOnMount: true,
                enabled:
                    !!dataObj.userId && !!dataObj.fromDate && !!dataObj.toDate,
            });

        const useTimesheetSubmitted = (dataObj) =>
            useQuery({
                queryKey: ["timesheetSubmitted", dataObj],
                queryFn: () => getTimesheetSubmittedApiCall(dataObj),
                enabled:
                    !!dataObj.employeeId &&
                    !!dataObj.fromDate &&
                    !!dataObj.toDate,
            });

        const useDepartmentAssignments = (dataObj) =>
            useQuery(
                ["dailyTimesheetDepartmentAssignments", dataObj],
                () => getDepartmentAssignmentsApiCall(dataObj),
                {
                    enabled:
                        !!dataObj.departmentId &&
                        !!dataObj.officeId &&
                        !!dataObj.agencyId &&
                        !!dataObj.fromDate,
                }
            );

        return {
            useAssignments,
            useDailyTotals,
            useRecentEntries,
            useTimesheetSubmitted,
            useDepartmentAssignments,
            updateHours: updateHoursMutation,
        };
    }, [
        getAssignmentsApiCall,
        getDailyTotalsApiCall,
        getRecentTimeEntriesApiCall,
        getTimesheetSubmittedApiCall,
        getDepartmentAssignmentsApiCall,
        updateHoursMutation,
    ]);
};
